import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChartLineUp, MagnifyingGlass, ArrowClockwise, User, Envelope, Clock, ChatCircleText, Folders } from "@phosphor-icons/react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all",           label: "All" },
  { key: "active_today",  label: "Active today" },
  { key: "active_7d",     label: "Last 7 days" },
  { key: "active_30d",    label: "Last 30 days" },
  { key: "never",         label: "Never signed in" },
];

const SORTS = [
  { key: "last_activity_at", label: "Last activity" },
  { key: "questions_asked",  label: "Total questions" },
  { key: "name",             label: "Name" },
];

const STATUS_LABEL = {
  active_today: "Active today",
  active_7d:    "Active this week",
  active_30d:   "Active this month",
  inactive:     "Inactive",
  never:        "Never signed in",
  unknown:      "Unknown",
};
const STATUS_TONE = {
  active_today: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  active_7d:    "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  active_30d:   "bg-amber-500/10 text-amber-700 border-amber-500/20",
  inactive:     "bg-ink-soft/10 text-ink-soft border-line",
  never:        "bg-gk-red/10 text-gk-red border-gk-red/20",
  unknown:      "bg-ink-soft/10 text-ink-soft border-line",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}
function relative(iso) {
  if (!iso) return "never";
  try {
    const d = new Date(iso);
    const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
    if (s < 86400 * 365) return `${Math.floor(s / 86400 / 30)}mo ago`;
    return `${Math.floor(s / 86400 / 365)}y ago`;
  } catch { return "—"; }
}

function SummaryCard({ label, value, icon }) {
  return (
    <div className="rounded-2xl border border-line bg-surface/40 p-4 flex items-center gap-3" data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-9 h-9 rounded-xl bg-gk-red/10 text-gk-red flex items-center justify-center">{icon}</div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-ink-soft">{label}</div>
        <div className="font-heading text-2xl leading-tight text-gk-charcoal truncate" data-testid={`summary-value-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
      </div>
    </div>
  );
}

export default function TeamUsage() {
  const [data, setData] = useState({ summary: null, users: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortKey, setSortKey] = useState("last_activity_at");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/analytics/team-usage");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load team usage");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filteredUsers = useMemo(() => {
    let rows = data.users || [];
    const query = q.trim().toLowerCase();
    if (query) {
      rows = rows.filter(r =>
        (r.email || "").toLowerCase().includes(query) ||
        (r.name || "").toLowerCase().includes(query),
      );
    }
    if (filter === "active_today") rows = rows.filter(r => r.status === "active_today");
    else if (filter === "active_7d") rows = rows.filter(r => r.status === "active_today" || r.status === "active_7d");
    else if (filter === "active_30d") rows = rows.filter(r => ["active_today","active_7d","active_30d"].includes(r.status));
    else if (filter === "never") rows = rows.filter(r => r.status === "never");
    rows = [...rows];
    rows.sort((a, b) => {
      if (sortKey === "name") return (a.name || a.email).localeCompare(b.name || b.email);
      if (sortKey === "questions_asked") return (b.questions_asked || 0) - (a.questions_asked || 0);
      // last_activity_at desc; null last
      const av = a.last_activity_at || ""; const bv = b.last_activity_at || "";
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return bv.localeCompare(av);
    });
    return rows;
  }, [data.users, q, filter, sortKey]);

  const summary = data.summary || {};

  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl mx-auto w-full">
      <div className="flex items-center gap-3 mb-2">
        <ChartLineUp size={22} weight="duotone" />
        <h1 className="font-heading text-4xl" data-testid="team-usage-heading">Team Usage</h1>
      </div>
      <p className="text-ink-soft mb-6">
        Who on the approved GK Team is actually using Ask Tracey. Aggregated from the existing chat activity — question text and personal auth data are never displayed.
      </p>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <SummaryCard label="Approved users"    value={loading ? "…" : (summary.total_approved ?? 0)}      icon={<User size={16} weight="fill" />} />
        <SummaryCard label="Signed in"         value={loading ? "…" : (summary.signed_in_count ?? 0)}     icon={<Envelope size={16} weight="fill" />} />
        <SummaryCard label="Active 7 days"     value={loading ? "…" : (summary.active_7d ?? 0)}           icon={<Clock size={16} weight="fill" />} />
        <SummaryCard label="Total questions"   value={loading ? "…" : (summary.total_questions ?? 0)}     icon={<ChatCircleText size={16} weight="fill" />} />
        <SummaryCard label="Total conversations" value={loading ? "…" : (summary.total_conversations ?? 0)} icon={<Folders size={16} weight="fill" />} />
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email"
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-line bg-surface/40 outline-none focus:border-gk-red text-sm"
            data-testid="team-usage-search"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                filter === f.key ? "bg-gk-red text-white border-gk-red" : "border-line text-ink-soft hover:bg-surface"
              }`}>{f.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-ink-soft">Sort:</span>
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} data-testid="team-usage-sort"
            className="px-2 py-1.5 rounded-lg border border-line bg-surface/40 text-sm">
            {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <Button variant="ghost" size="sm" onClick={load} data-testid="team-usage-refresh" className="text-ink-soft">
            <ArrowClockwise size={14} weight="bold" /> Refresh
          </Button>
        </div>
      </div>

      {/* User table */}
      <div className="rounded-2xl border border-line overflow-hidden bg-surface/20" data-testid="team-usage-table">
        <div className="hidden md:grid grid-cols-[minmax(0,2fr)_100px_140px_140px_120px_100px_140px] bg-surface/60 px-4 py-2 text-[11px] uppercase tracking-widest text-ink-soft">
          <div>User</div>
          <div>Role</div>
          <div>Last activity</div>
          <div>Last sign-in</div>
          <div>Questions</div>
          <div>Chats</div>
          <div>Status</div>
        </div>
        {loading ? (
          <div className="p-8 text-ink-soft text-center">Loading…</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-ink-soft text-center" data-testid="team-usage-empty">No users match this filter.</div>
        ) : (
          filteredUsers.map((u) => (
            <div key={u.email} data-testid={`team-usage-row-${u.email}`}
              className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_100px_140px_140px_120px_100px_140px] px-4 py-3 border-t border-line items-center gap-2">
              <div className="min-w-0">
                <div className="font-medium text-gk-charcoal truncate">{u.name || "—"}</div>
                <div className="text-xs text-ink-soft truncate">{u.email}</div>
              </div>
              <div className="text-xs">
                <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest ${u.role === "manager" ? "bg-gk-red/10 text-gk-red border-gk-red/20" : "bg-surface text-ink-soft border-line"}`}>
                  {u.role}
                </span>
              </div>
              <div className="text-xs" title={fmt(u.last_activity_at)}>{relative(u.last_activity_at)}</div>
              <div className="text-xs" title={fmt(u.last_login_at)}>{u.last_login_at ? relative(u.last_login_at) : (u.signed_in ? "—" : "never")}</div>
              <div className="text-sm font-medium">{u.questions_asked ?? 0}</div>
              <div className="text-sm">{u.conversations ?? 0}</div>
              <div>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] border ${STATUS_TONE[u.status] || STATUS_TONE.unknown}`} data-testid={`row-status-${u.email}`}>
                  {STATUS_LABEL[u.status] || "Unknown"}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-4 text-[11px] text-ink-soft">
        Aggregated from existing chat activity. Question content, tokens, and personal auth data are never included on this page.
        {summary.generated_at ? ` · Refreshed ${relative(summary.generated_at)}.` : ""}
      </div>
    </div>
  );
}
